import React, { useState, useEffect } from 'react';
import { ref, onValue, getDatabase } from 'firebase/database';
import { useCurrentUid } from "../hooks/useCurrentUid";
import { Truck, Calendar, MapPin, AlertCircle, FileText } from 'lucide-react';
import "./TransportGoods.css";
import { MdInfo } from 'react-icons/md';

const TransportGoods = () => {
    const userId = useCurrentUid();
    const [dispatches, setDispatches] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        const db = getDatabase();
        const dispatchRef = ref(db, `Transport-dispatches/${userId}`);

        const unsubscribe = onValue(dispatchRef, (snapshot) => {
            if (snapshot.exists()) {
                const list = Object.entries(snapshot.val()).map(([id, data]) => ({
                    id,
                    ...data,
                }));
                list.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
                setDispatches(list);
            } else {
                setDispatches([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId]);

    const downloadFile = (url, fileName) => {
        if (url) {
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.download = fileName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    if (loading) {
        return (
            <div className="tg-loading-container">
                <div className="tg-loader"></div>
            </div>
        );
    }

    const transinfo = ()=>{
      alert(
            "Transport Goods Information\n\n" +
            "• This window shows all your transport dispatch requests.\n" +
            "• You can view details like transporter name, route (From → To), dispatch & delivery dates.\n" +
            "• If any issue was reported during dispatch, it will be highlighted.\n" +
            "• You can download attached documents (Bill, Statement, Credit Note) directly from here.\n\n" +
            "All data is updated in real-time from the admin panel.\n" +
            "Contact support if you have any questions."
        );
    }
    return (
        <div className="tg-transport-goods-page">
            <div className="tg-page-header">
               
                <div>
                    <h1 className="tg-main-title">Transport 
                       <Truck size={25} className="tg-header-icon" />
                      <MdInfo className='icon-i' onClick={transinfo}/>
                      
                    </h1>
                    <p className="tg-subtitle">Your Complete Transport Dispatch History</p>
                </div>
            </div>

            <div className="tg-main-container">
                {dispatches.length === 0 ? (
                    <div className="tg-empty-state">
                        <Truck size={85} className="tg-empty-icon" />
                        <h2>No Transport Dispatches Yet</h2>
                        <p>You haven't submitted any transport requests.</p>
                    </div>
                ) : (
                    <div className="tg-dispatch-list">
                        {dispatches.map((dispatch, index) => (
                            <div key={dispatch.id} className="tg-dispatch-item">
                                <div className="tg-item-header">
                                    <div className="tg-item-number">#{index + 1}</div>
                                    <div className="tg-item-date">
                                        {new Date(dispatch.timestamp).toLocaleDateString('en-IN', {
                                            day: 'numeric',
                                            month: 'long',
                                            year: 'numeric'
                                        })}
                                    </div>
                                </div>

                                <div className="tg-transporter-row">
                                    <strong>Transporter:</strong> 
                                    <span className="tg-transporter-name">{dispatch.transporterName || 'N/A'}</span>
                                </div>

                                <div className="tg-route-row">
                                    <MapPin size={20} className="tg-map-icon" />
                                    <span className="tg-route-text">
                                        {dispatch.routeFrom} <span className="tg-arrow">→</span> {dispatch.routeTo}
                                    </span>
                                </div>

                                <div className="tg-dates-grid">
                                    <div className="tg-date-box">
                                        <Calendar size={18} />
                                        <div>
                                            <small>Dispatch Date</small>
                                            <p>{dispatch.dispatchDate || 'Not specified'}</p>
                                        </div>
                                    </div>
                                    <div className="tg-date-box">
                                        <Calendar size={18} />
                                        <div>
                                            <small>Expected Delivery</small>
                                            <p>{dispatch.deliveryDate || 'Not specified'}</p>
                                        </div>
                                    </div>
                                </div>

                                {dispatch.hasIssue && (
                                    <div className="tg-issue-row">
                                        <AlertCircle size={20} />
                                        <span>Issue Reported: </span>
                                        {dispatch.issueNote && <p className="tg-issue-note">{dispatch.issueNote}</p>}
                                    </div>
                                )}

                                {dispatch.files && Object.keys(dispatch.files).length > 0 && (
                                    <div className="tg-documents-row">
                                        <strong>Documents:</strong>
                                        <div className="tg-files">
                                            {Object.entries(dispatch.files).map(([key, url]) => (
                                                <button
                                                    key={key}
                                                    className="tg-file-btn"
                                                    onClick={() => downloadFile(url, `${key}-${dispatch.id}`)}
                                                >
                                                    <FileText size={18} />
                                                    {key.charAt(0).toUpperCase() + key.slice(1)}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default TransportGoods;